import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { TokenService, LogService, UtilsService } from 'abp-ng2-module';
import { AppConsts } from '@shared/AppConsts';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import {
    AuthenticateModel,
    AuthenticateResultModel,
    TokenAuthServiceProxy,
} from '@shared/service-proxies/service-proxies';

@Injectable()
export class AppAuthService {
    authenticateModel: AuthenticateModel;
    authenticateResult: AuthenticateResultModel;
    rememberMe: boolean;

    constructor(
        private _tokenAuthService: TokenAuthServiceProxy,
        private _router: Router,
        private _utilsService: UtilsService,
        private _tokenService: TokenService,
        private _logService: LogService
    ) {
        this.clear();
    }

    authenticate(finallyCallback?: () => void): void {
        finallyCallback = finallyCallback || (() => { });

        this._tokenAuthService
            .authenticate(this.authenticateModel)
            .pipe(
                finalize(() => {
                    finallyCallback();
                })
            )
            .subscribe((result: AuthenticateResultModel) => {
                this.processAuthenticateResult(result);
            });
    }

    private processAuthenticateResult(
        authenticateResult: AuthenticateResultModel
    ) {
        this.authenticateResult = authenticateResult;

        if (authenticateResult.accessToken) {
            // Successfully logged in
            this.login(
                authenticateResult.accessToken,
                authenticateResult.encryptedAccessToken,
                authenticateResult.expireInSeconds,
                this.rememberMe
            );
        } else {
            // Unexpected result!

            this._logService.warn('Unexpected authenticateResult!');
            this._router.navigate(['account/login']);
        }
    }

    private login(
        accessToken: string,
        encryptedAccessToken: string,
        expireInSeconds: number,
        rememberMe?: boolean
    ): void {
        const tokenExpireDate = new Date(new Date().getTime() + 1000 * expireInSeconds);

        if (rememberMe) {
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('tokenExpireDate', tokenExpireDate.toString());
            localStorage.setItem('rememberMe', 'true');
            localStorage.setItem('username', this.authenticateModel.userNameOrEmailAddress);
            localStorage.setItem('password', this.authenticateModel.password);
        } else {
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('username');
            localStorage.removeItem('password');

            sessionStorage.setItem('accessToken', accessToken);
            sessionStorage.setItem('tokenExpireDate', tokenExpireDate.toString());
        }

        this._tokenService.setToken(accessToken, tokenExpireDate);

        this._utilsService.setCookieValue(
            AppConsts.authorization.encryptedAuthTokenName,
            encryptedAccessToken,
            tokenExpireDate,
            abp.appPath
        );

        let initialUrl = UrlHelper.initialUrl;
        if (initialUrl.indexOf('/login') > 0) {
            initialUrl = AppConsts.appBaseUrl;
        }

        location.href = initialUrl;
    }

    logout(reload?: boolean): void {
        abp.auth.clearToken();
        abp.utils.deleteCookie(AppConsts.authorization.encryptedAuthTokenName);

        // Chỉ xóa token và ngày hết hạn, giữ lại thông tin đăng nhập nếu có
        localStorage.removeItem('accessToken');
        localStorage.removeItem('tokenExpireDate');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('tokenExpireDate');

        if (reload !== false) {
            location.href = AppConsts.appBaseUrl;
        }
    }

    getSavedCredentials(): { username: string, password: string, rememberMe: boolean } | null {
        const username = localStorage.getItem('username');
        const password = localStorage.getItem('password');
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        if (username && password && rememberMe) {
            return { username, password, rememberMe };
        }
        return null;
    }

    private clear(): void {
        this.authenticateModel = new AuthenticateModel();
        this.authenticateModel.rememberClient = false;
        this.authenticateResult = null;
        this.rememberMe = false;
    }
}
